/**
 * ColumnPickerV2 — Thin adapter over the design system's ColumnPicker.
 *
 * Preserves the existing API (handleUser, handleSubmit, onSave) for
 * backward compatibility while delegating all rendering, positioning,
 * and styling to @packages/ui's ColumnPicker pattern.
 */

import React, { useCallback } from 'react';
import { ColumnPicker } from '@packages/ui/patterns/ColumnPicker';
import { TABLE_IDS } from '../semantic-contract';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ColumnOption {
  label: string;
  value: string;
}

export interface ColumnPickerV2Props {
  data: ColumnOption[];
  checkedList: string[];
  handleUser: (columnValue: string) => void;
  handleSubmit?: () => void;
  onSave?: (finalList: string[]) => void;
  width?: string;
  padding?: string;
  contentHeight?: string;
  loading?: boolean;
  triggerLabel?: string;
  triggerIcon?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

const ColumnPickerV2 = ({
  data,
  checkedList,
  handleUser,
  handleSubmit,
  onSave,
  loading,
  triggerLabel,
  triggerIcon,
}: ColumnPickerV2Props): JSX.Element => {
  const handleColumnSave = useCallback(
    (committed: string[]) => {
      if (onSave) {
        onSave(committed);
      } else {
        const added = committed.filter((v) => !checkedList.includes(v));
        const removed = checkedList.filter((v) => !committed.includes(v));
        [...added, ...removed].forEach((v) => handleUser(v));
        handleSubmit?.();
      }
    },
    [checkedList, handleUser, handleSubmit, onSave],
  );

  return (
    <ColumnPicker
      columns={data}
      checkedValues={checkedList}
      onSave={handleColumnSave}
      loading={loading}
      triggerLabel={triggerLabel}
      triggerIcon={triggerIcon}
      semanticId={TABLE_IDS.columnPicker}
      legacyCompat={{
        triggerId: 'dropdownId',
        panelClassName: 'dropdownListContainer',
        useLegacyRowDom: true,
      }}
    />
  );
};

export default ColumnPickerV2;
